import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { logger } from '../../lib/logger';

export const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
  { value: 'inactive', label: 'Inactive', color: 'bg-slate-100 text-slate-600' },
  { value: 'suspended', label: 'Suspended', color: 'bg-red-100 text-red-700' },
  { value: 'pending_approval', label: 'Pending Approval', color: 'bg-amber-100 text-amber-700' },
  { value: 'rejected', label: 'Rejected', color: 'bg-rose-100 text-rose-700' },
  { value: 'demo', label: 'Demo', color: 'bg-purple-100 text-purple-700' },
];

const blankFormData = () => ({
  name: '', npi: '', tax_id: '',
  address: { street1: '', street2: '', city: '', state: 'OH', zip_code: '', county: '' },
  contact: { name: '', title: '', phone: '', email: '', fax: '' },
  contracts: {
    medicaid: { enabled: false, provider_id: '' },
    embs: { enabled: false, contract_number: '' },
    sandata: { enabled: false, provider_id: '' },
    bh: { enabled: false, provider_id: '' },
  },
  payment_config: {
    payment_method: 'ach', billing_email: '', auto_pay: false,
    bank_name: '', routing_number: '', account_number: '', account_type: 'checking',
  },
  status: 'active', notes: '',
});

const buildContractsPayload = (contracts) => {
  const out = [];
  if (contracts.medicaid.enabled) {
    out.push({ contract_type: 'medicaid', enabled: true, provider_id: contracts.medicaid.provider_id });
  }
  if (contracts.embs.enabled) {
    out.push({ contract_type: 'embs', enabled: true, contract_number: contracts.embs.contract_number });
  }
  if (contracts.sandata.enabled) {
    out.push({ contract_type: 'sandata', enabled: true, provider_id: contracts.sandata.provider_id });
  }
  if (contracts.bh.enabled) {
    out.push({ contract_type: 'bh', enabled: true, provider_id: contracts.bh.provider_id });
  }
  return out;
};

const hydrateFormFromAgency = (agency) => {
  const findContract = (type) => agency.contracts?.find((c) => c.contract_type === type) || {};
  return {
    name: agency.name || '',
    npi: agency.npi || '',
    tax_id: agency.tax_id || '',
    address: agency.address || { street1: '', street2: '', city: '', state: 'OH', zip_code: '', county: '' },
    contact: agency.contact || { name: '', title: '', phone: '', email: '', fax: '' },
    contracts: {
      medicaid: { enabled: findContract('medicaid').enabled || false, provider_id: findContract('medicaid').provider_id || '' },
      embs: { enabled: findContract('embs').enabled || false, contract_number: findContract('embs').contract_number || '' },
      sandata: { enabled: findContract('sandata').enabled || false, provider_id: findContract('sandata').provider_id || '' },
      bh: { enabled: findContract('bh').enabled || false, provider_id: findContract('bh').provider_id || '' },
    },
    payment_config: agency.payment_config || { payment_method: 'ach', billing_email: '', auto_pay: false },
    status: agency.status || 'active',
    notes: agency.notes || '',
  };
};

/**
 * All non-UI state for AgencyManagementPage: list fetch + filter, signup approvals,
 * CRUD + form data. Extracted to keep the page itself a thin composition.
 */
export function useAgencies({ refetchAgenciesContext } = {}) {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pendingSignups, setPendingSignups] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [formData, setFormData] = useState(blankFormData);

  const loadAgencies = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      const res = await api.get('/agencies/', { params });
      setAgencies(res.data.agencies || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Only AZAI staff can manage agencies.');
      } else {
        toast.error('Failed to load agencies');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  const loadPendingSignups = useCallback(async () => {
    try {
      const res = await api.get('/agencies/pending-signups');
      setPendingSignups(res.data.agencies || []);
    } catch (e) {
      logger.error('Failed to load pending signups', e);
    }
  }, []);

  useEffect(() => { loadAgencies(); }, [loadAgencies]);
  useEffect(() => { loadPendingSignups(); }, [loadPendingSignups]);

  const refetchAll = () => {
    loadAgencies();
    refetchAgenciesContext?.();
  };

  const approveSignup = async (id) => {
    try {
      await api.post(`/agencies/${id}/approve`);
      toast.success('Agency approved and activated');
      loadPendingSignups();
      loadAgencies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    }
  };

  const rejectSignup = async (id) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await api.post(`/agencies/${id}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success('Agency rejected');
      loadPendingSignups();
      loadAgencies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    }
  };

  const openCreate = () => {
    setFormData(blankFormData());
    setCreateOpen(true);
  };

  const openEdit = (agency) => {
    setSelectedAgency(agency);
    setFormData(hydrateFormFromAgency(agency));
    setEditOpen(true);
  };

  const openView = (agency) => {
    setSelectedAgency(agency);
    setViewOpen(true);
  };

  const handleCreate = async () => {
    try {
      await api.post('/agencies/', { ...formData, contracts: buildContractsPayload(formData.contracts) });
      toast.success('Agency created successfully');
      setCreateOpen(false);
      setFormData(blankFormData());
      refetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agency');
    }
  };

  const handleUpdate = async () => {
    if (!selectedAgency) return;
    try {
      await api.put(`/agencies/${selectedAgency.id}`, {
        name: formData.name,
        npi: formData.npi,
        tax_id: formData.tax_id,
        address: formData.address,
        contact: formData.contact,
        contracts: buildContractsPayload(formData.contracts),
        notes: formData.notes,
      });
      toast.success('Agency updated successfully');
      setEditOpen(false);
      refetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update agency');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this agency?')) return;
    try {
      await api.delete(`/agencies/${id}`);
      toast.success('Agency deactivated');
      refetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate agency');
    }
  };

  return {
    agencies, loading, searchTerm, setSearchTerm, statusFilter, setStatusFilter,
    pendingSignups, approveSignup, rejectSignup,
    createOpen, setCreateOpen, editOpen, setEditOpen, viewOpen, setViewOpen,
    selectedAgency, formData, setFormData,
    openCreate, openEdit, openView,
    handleCreate, handleUpdate, handleDelete,
    reload: refetchAll,
  };
}
